<script lang="ts">
    import './style.css'

    import CircularProgressBar from './components/CircularProgressBar.svelte'

    import QuestionnairePage from './components/questions/QuestionnairePage.svelte'

    import type { Questionnaire } from './types/Questionnaire'
    import ResultsPage from './components/results/ResultsPage.svelte'
    import { Answer } from './types/Answer'
    import { onMount } from 'svelte'
    import type { State } from './types/State'
    import Popup from './components/Popup.svelte'

    export let id: string
    export let leichtesprache: string
    let simpleLanguage: boolean

    let questionnaire: Questionnaire | null = null

    let state: State = {}

    let progresses: number[] = []

    let currentField = 0

    async function init() {
        if (questionnaire !== null) return

        let oldState: State = {}
        if (localStorage.getItem('selfcheck-expiration')) {
            const exp = parseInt(localStorage.getItem('selfcheck-expiration')!)
            if (exp > Date.now()) {
                oldState = JSON.parse(localStorage.getItem('selfcheck-state') ?? '{}')
            } else {
                localStorage.removeItem('selfcheck-state')
                localStorage.removeItem('selfcheck-expiration')
                localStorage.removeItem('selfcheck-sent')
            }
        }
        state = {}

        // Use for testing:
        // const data = await (await fetch('/assets/data.json')).json()
        const data = await (await fetch(`/wp-json/wp/v2/selbstcheck2/${id}`)).json()

        let q = {
            fields: [],
        }
        q.fields = data.acf.frageboegen.map((f: any) => {
            return {
                title: f.titel,
                icon: f.icon,
                link: f.link,
                description: f.beschreibung,
                sections: f.fragenkatalog.map((fs: any) => {
                    return {
                        description: fs.beschreibung,
                        subsections: fs.frage.map((fss: any) => {
                            return {
                                description: fss.beschreibung,
                                statements: fss.teilfrage.map((fsss: any) => {
                                    return {
                                        uuid: fsss.uuid,
                                        description: fsss.beschreibung,
                                    }
                                }),
                            }
                        }),
                    }
                }),
            }
        })

        questionnaire = q as Questionnaire

        let firstFieldWithUnanswered = questionnaire?.fields.length ?? 1
        questionnaire?.fields.forEach((f, fI) => {
            f.sections.forEach((s, sI) => {
                s.subsections.forEach((ss, ssI) => {
                    ss.statements.forEach((st, stI) => {
                        state[st.uuid] = {
                            fieldIndex: fI,
                            sectionIndex: sI,
                            subsectionIndex: ssI,
                            statementIndex: stI,
                        }
                        if (st.uuid in oldState) {
                            if (!('answer' in oldState[st.uuid]) && fI < firstFieldWithUnanswered) {
                                firstFieldWithUnanswered = fI
                            } else {
                                state[st.uuid].answer = oldState[st.uuid].answer
                            }
                        } else {
                            if (fI < firstFieldWithUnanswered) {
                                firstFieldWithUnanswered = fI
                            }
                        }
                    })
                })
            })
        })
        currentField = firstFieldWithUnanswered
    }

    onMount(() => {
        init()

        simpleLanguage = leichtesprache === 'true'

        const listener = () => {
            const hash = location.hash
            if (/#handlungsfeld-[0-9]+/.test(hash)) {
                const field = parseInt(hash.substring(hash.indexOf('-') + 1)) - 1
                if (field < (questionnaire?.fields.length ?? 0)) {
                    currentField = field
                }
            } else if (hash === '#ergebnisse') {
                currentField = questionnaire?.fields.length ?? 1
            }
        }
        window.addEventListener('hashchange', listener)
        return () => {
            window.removeEventListener('hashchange', listener)
        }
    })

    $: updateProgresses(questionnaire)

    $: {
        if (Object.keys(state).length > 0) {
            localStorage.setItem('selfcheck-state', JSON.stringify(state))
            localStorage.setItem(
                'selfcheck-expiration',
                (Date.now() + 30 * 24 * 60 * 60 * 1000).toString(),
            )
        }
    }

    function updateProgresses(questionnaire: Questionnaire | null) {
        progresses = questionnaire?.fields.map((_, i) => getProgress(i)) ?? []
    }

    function getProgress(fieldIndex: number) {
        const answered = Object.values(state).filter(
            (it) => it.fieldIndex === fieldIndex && it.answer !== undefined,
        ).length
        const total = Object.values(state).filter((it) => it.fieldIndex === fieldIndex).length
        return answered / total
    }

    let showMissingAnswerPopup = false
</script>

<svelte:options tag="ewiko-selfcheck" />

<!-- <link rel="stylesheet" href="/build/bundle.css" /> -->
<link rel="stylesheet" href="/plus-ewiko/wp-content/plugins/ewiko-selfcheck/build/bundle.css" />
<link rel="stylesheet" href="/plus-ewiko/wp-content/themes/aok/style.css" />

{#if questionnaire}
    {#if questionnaire.fields.length > 1}
        <div class="progress">
            <div class="progress-circles">
                {#each questionnaire.fields as _, index}
                    <div class="progress-circle" on:click={() => (currentField = index)}>
                        <CircularProgressBar
                            progress={progresses[index]}
                            text={(index + 1).toString()}
                        />
                    </div>
                {/each}
            </div>
            <div>
                Status: {progresses.filter((it) => it === 1).length} von {questionnaire.fields
                    .length} Handlungsfelder wurden beantwortet
            </div>
        </div>
    {/if}

    {#if currentField < questionnaire.fields.length}
        <QuestionnairePage
            {simpleLanguage}
            bind:field={questionnaire.fields[currentField]}
            path={[currentField]}
            bind:state
        />
        <div class="buttons">
            {#if currentField > 0}
                <button class="elementor-button back outline" on:click={() => currentField--}>
                    Zurück
                </button>
            {/if}
            {#if currentField + 1 < questionnaire.fields.length}
                <button class="elementor-button forward" on:click={() => currentField++}>
                    Nächster Schritt
                </button>
            {:else}
                <div class="popup-wrapper">
                    <button
                        class="elementor-button forward"
                        class:disabled={progresses.find((it) => it !== 1) !== undefined}
                        on:click={() => {
                            if (progresses.find((it) => it !== 1) !== undefined) {
                                showMissingAnswerPopup = true
                            } else {
                                currentField++
                            }
                        }}
                    >
                        Zur Auswertung
                    </button>
                    {#if showMissingAnswerPopup}
                        <Popup on:dismiss={() => (showMissingAnswerPopup = false)}>
                            Achtung, einige Fragen wurden noch nicht vollständig beantwortet. Erst
                            danach kann der Selbstcheck fortgeführt werden.
                        </Popup>
                    {/if}
                </div>
            {/if}
        </div>
        {#if questionnaire.fields.length > 1}
            <div class="dots">
                {#each questionnaire.fields as _, i}
                    <div
                        class="dot"
                        class:active={i === currentField}
                        on:click={() => (currentField = i)}
                    />
                {/each}
            </div>
        {/if}
    {:else}
        <ResultsPage {questionnaire} {state} {simpleLanguage} />
    {/if}
{/if}

<style lang="scss">
    .progress {
        color: var(--color-primary-light);

        .progress-circles {
            overflow-x: auto;
            display: flex;
            flex-direction: row;
            column-gap: 1.5rem;
            row-gap: 1rem;
            margin-bottom: 1rem;

            .progress-circle {
                min-width: 5.625rem;
                width: 5.625rem;
                cursor: pointer;
            }
        }
    }

    .buttons {
        display: flex;
        flex-direction: row;
        justify-content: center;
        column-gap: 2.375rem;
        margin-bottom: 3.17rem;

        .forward {
            &.disabled {
                opacity: 0.5;
            }
        }
    }

    .dots {
        display: flex;
        flex-direction: row;
        column-gap: 1rem;
        justify-content: center;
        margin-bottom: 7rem;

        .dot {
            cursor: pointer;
            width: 1.25rem;
            height: 1.25rem;
            border-radius: 50%;
            position: relative;
            border: solid 1px var(--color-gray-dark);

            &.active {
                background-color: var(--color-primary);
                border: solid 1px var(--color-primary);
            }
        }
    }

    .popup-wrapper {
        position: relative;
    }

    @media (max-width: 790px) {
        .progress {
            margin-left: 0rem;
            margin-right: 0rem;
        }
    }
</style>
